import React, { useCallback, useRef } from 'react';
import { PostModel } from '../../data-layer/posts/types';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { InfiniteGrid } from '../../ui/layout/InfiniteGrid';
import { PostCardCompact, PostCardCompactSkeleton } from '../../ui/posts/PostCardCompact';
import { Typography } from '../../ui/typography/Typography';
import { analyticsService, EventSource } from '../../utils/analyticsService';
import { ymTrackSearchGoToPost } from '../../utils/metrika';
import { UsePostsCollectionPayload } from '../post/usePostCollection';
import { useCurrentUser } from '../users/useCurrentUser';

const COLUMNS = 3;
const PER_PAGE = 24;
// Показываем на одну строчку скелетонов меньше, чтобы провоцировать InfiniteGrid на подгрузку следующей пачки при быстром скролле
const SKELETONS_PER_PAGE = PER_PAGE - COLUMNS;

interface SearchPostProps {
    postCollection: UsePostsCollectionPayload;
}

export const SearchPost = (props: SearchPostProps) => {
    const { postCollection } = props;
    const currentUser = useCurrentUser();
    const skeletonCountRef = useRef(0);

    const onPostCardClick = useCallback(
        (id: PostModel['id'], postNumber: number) => {
            ymTrackSearchGoToPost({
                page: 'Поиск',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            analyticsService.trackGridTap({
                eventSource: EventSource.SEARCH,
                postId: id,
                postNumber,
            });
        },
        [currentUser?.login, currentUser?.profile?.publicId],
    );

    if (!postCollection.isLoading) {
        skeletonCountRef.current = 0;
    }

    return postCollection.isLoading || postCollection.items.length > 0 ? (
        <InfiniteGrid
            columns={COLUMNS}
            perPage={PER_PAGE}
            count={postCollection.items.length}
            isLoading={postCollection.isLoading}
            isEndReached={postCollection.isEndReached}
            onLoadNext={postCollection.onLoadNext}
            renderItem={(index: number) => {
                const postModel = postCollection.items[index];

                if (postModel) {
                    return (
                        <Box>
                            <PostCardCompact
                                post={toPostUI(postModel)}
                                onPostCardClick={onPostCardClick}
                                index={index}
                            />
                        </Box>
                    );
                }

                if (postCollection.isLoading && skeletonCountRef.current < SKELETONS_PER_PAGE) {
                    skeletonCountRef.current++;

                    return (
                        <Box>
                            <PostCardCompactSkeleton />
                        </Box>
                    );
                }

                return null;
            }}
        />
    ) : (
        <Flex px={3} alignItems="flex-start" justifyContent="center" flexGrow={1} spacing={1}>
            <Typography variant="title2" weight="bold">
                Ничего не найдено
            </Typography>
            <Typography>
                Попробуйте другой запрос. <br />
                Например «Кроссовки» или «Путешествия»
            </Typography>
        </Flex>
    );
};
